<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onMount } from 'svelte';
    import { __, getActualKey } from '@oat-sa-private/ui-core';
    import { Icon, IconBarButton } from '@oat-sa-private/ui-elements';
    import { isPreviewSupportedFormat } from './util.js';

    /**
     * Component is used to show attachments list
     */

    /**
     * @typedef {Object} Props
     * @property {import('./plugin.js').Attachment[]} attachments
     * @property {Boolean} [showNewTabLinks]
     * @property {Function} onClick
     * @property {Function} onClose
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        attachments,
        showNewTabLinks = true,
        onClick,
        onClose
    } = $props();

    /**
     * Action button click handler
     * @param {String} id
     * @param {Boolean} inNewTab
     */
    function handleAttachmentClick(id, inNewTab) {
        onClick?.({ id, inNewTab });
    }

    /**
     * Keydown handler
     * @param {KeyboardEvent} e event
     */
    function handleKeyDown(e) {
        const pressedKey = getActualKey(e);
        switch (pressedKey) {
            case 'esc': {
                onClose?.();
                break;
            }
        }
    }

    let ulElt = $state();

    onMount(() => {
        ulElt?.querySelector('button')?.focus();
    });
</script>

<style>
    ul {
        margin: 0;
        padding: 0 2rem;
        list-style: none;
        background: var(--color-bg-default);

        & li {
            padding: 2rem 0;
            display: flex;
            justify-content: space-between;

            & + li {
                border-top: var(--border-thin) solid var(--color-gs-light-secondary);
            }

            &:first-child {
                border-top-left-radius: var(--radius-large);
                border-top-right-radius: var(--radius-large);
            }
            &:last-child {
                border-bottom-left-radius: var(--radius-large);
                border-bottom-right-radius: var(--radius-large);
            }

            & .attachment-file {
                position: relative;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                height: 5.5rem;
                width: auto;
                padding-left: var(--space-1x5);
                padding-right: var(--space-1x5);
                color: var(--color-text-default);
                border-radius: var(--radius-pill);
            }

            & .attachment-file .label {
                font-size: var(--fontsize-body-xs);
                margin-inline-start: var(--space-1x);
                font-weight: bold;
            }

            & .attachment-file .label,
            & :global(.icon-bar-btn .label) {
                text-transform: none;
                text-align: left;
                /* truncate text */
                width: 24ch;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
</style>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<ul class="attachments-list" bind:this={ulElt} onkeydown={handleKeyDown}>
    {#each attachments as attachment (attachment.id)}
        <li>
            <span class="attachment-file">
                <Icon name="attachment-16" ariaHidden={true} />
                <span class="label">{attachment.name}</span>
            </span>

            {#if isPreviewSupportedFormat(attachment.type)}
                <IconBarButton
                    ariaLabel={__('Open attachment %s', attachment.name)}
                    dataTestId="attachment-preview"
                    icon="eye-16"
                    shape="pill"
                    on:click={() => handleAttachmentClick(attachment.id, false)} />
            {/if}
            {#if showNewTabLinks}
                <IconBarButton
                    ariaLabel={__('Open attachment %s in a new tab', attachment.name)}
                    dataTestId="attachment-download"
                    icon="download-16"
                    shape="pill"
                    on:click={() => handleAttachmentClick(attachment.id, true)} />
            {/if}
        </li>
    {/each}
</ul>
